import { giftWrapEventListener } from '../../services/giftWrapEventListener';
import { publish } from '../../../../base/pubsub';
import { PUB_SUB_EVENTS } from '../../../../base/constants';
import { getGiftWrapKey, hasStoryOfUsLines, isChildLine, isPackagingChild } from './lineRole';

/**
 * "Remove gifting options": removes every child line for the key in one /cart/update.js, then clears the
 * key from the parent with /cart/change.js (only _giftwrap_key; _vendorRoutingMatch and everything else stay).
 * A Story of Us card and envelope keyed to the piece are not gift packaging (ALBAU-909): they stay, and so does the
 * piece's key while they are in the cart.
 * Children go first: if the second request fails, the parent is left with a dangling key, which the orphan
 * cleanup already strips; the reverse order would leave invisible, charged child lines.
 * Calls are serialised (module-level chain): the parent is addressed by line index, so a second Remove must read
 * a fresh cart after the first has finished. If the key clear fails after the children went, the drawer is still
 * refreshed from the removal and the error is rethrown.
 * @param {{ giftWrapKey: string, listener?: typeof giftWrapEventListener }} args
 * @returns {Promise<void>}
 */
export function removeGiftOptions({ giftWrapKey, listener = giftWrapEventListener }) {
  if (!giftWrapKey) return Promise.resolve();
  const run = queue.then(() => removeOne(giftWrapKey, listener));
  queue = run.catch(() => {});
  return run;
}

/** Tail of the call chain; settles (never rejects) when the latest call is done. */
let queue = Promise.resolve();

/** Refreshes the drawer from a /cart request's sections and tells the rest of the theme the cart changed. */
async function refreshDrawer(response) {
  const html = response?.sections?.['cart-drawer'];
  if (window.CartDrawer?.updateCart) await window.CartDrawer.updateCart(html ? { html } : undefined);
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
}

async function removeOne(giftWrapKey, listener) {
  listener.isOperationInProgress = true;
  try {
    const cart = await listener.getCurrentCartData();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const childKeys = items
      .filter((item) => isPackagingChild(item) && String(item.properties?._giftwrap_key) === giftWrapKey)
      .map((item) => item.key);

    let latest = null;
    if (childKeys.length > 0) latest = await listener.removeCartItems(childKeys);

    const lines = Array.isArray(latest?.items) ? latest.items : items;
    const parentIndex = hasStoryOfUsLines(lines, giftWrapKey)
      ? -1
      : lines.findIndex((item) => !isChildLine(item) && getGiftWrapKey(item.properties) === giftWrapKey);
    if (parentIndex !== -1) {
      try {
        latest = await listener.clearGiftWrapKey(parentIndex + 1, lines[parentIndex].properties || {});
      } catch (error) {
        if (childKeys.length > 0) await refreshDrawer(latest);
        throw error;
      }
    }

    await refreshDrawer(latest);
  } finally {
    listener.isOperationInProgress = false;
  }
}
